import { useState } from 'react'
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Stepper,
    Step,
    StepLabel,
    StepContent,
} from '@mui/material'
import {
    CheckCircle,
    Cancel,
    HourglassEmpty,
    Comment,
    ThumbUp,
    ThumbDown,
} from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { approvalApi } from '@api/endUser'
import { useTranslation } from '@i18n'
import { toast } from 'react-toastify'

/**
 * Approval Timeline Component
 * Shows the approval status of each department
 */
export const ApprovalTimeline = ({ approvals, isRTL }) => {
    if (!approvals || approvals.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {isRTL ? 'لا توجد موافقات بعد' : 'No approvals yet'}
            </Typography>
        )
    }

    const getStepIcon = (status) => {
        switch (status) {
            case 'approved':
                return <CheckCircle color="success" />
            case 'denied':
                return <Cancel color="error" />
            default:
                return <HourglassEmpty color="warning" />
        }
    }

    return (
        <Stepper orientation="vertical" activeStep={-1}>
            {approvals.map((approval, index) => (
                <Step key={approval.id} completed={approval.status !== 'pending'}>
                    <StepLabel
                        StepIconComponent={() => getStepIcon(approval.status)}
                        optional={
                            approval.approved_at && (
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(approval.approved_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                                </Typography>
                            )
                        }
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">
                                    {isRTL ? approval.department_name_ar : approval.department_name_en}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={
                                        approval.status === 'approved'
                                            ? (isRTL ? 'موافق' : 'Approved')
                                            : approval.status === 'denied'
                                                ? (isRTL ? 'مرفوض' : 'Denied')
                                                : (isRTL ? 'قيد الانتظار' : 'Pending')
                                    }
                                    color={
                                        approval.status === 'approved' ? 'success'
                                            : approval.status === 'denied' ? 'error'
                                                : 'warning'
                                    }
                                />
                            </Box>
                            {/* Always show who took action if available */}
                            {(approval.approved_by_name_en || approval.approved_by_name_ar) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ThumbUp sx={{ fontSize: 14 }} />
                                    {isRTL
                                        ? `بواسطة: ${approval.approved_by_name_ar || approval.approved_by_name_en}`
                                        : `By: ${approval.approved_by_name_en || approval.approved_by_name_ar}`
                                    }
                                </Typography>
                            )}
                        </Box>
                    </StepLabel>
                    {approval.note && (
                        <StepContent>
                            <Paper sx={{ p: 1.5, bgcolor: 'background.default' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {approval.note}
                                </Typography>
                            </Paper>
                        </StepContent>
                    )}
                </Step>
            ))}
        </Stepper>
    )
}

/**
 * Department Approval Action Panel
 * Allows department users to approve/deny submissions
 */
const DepartmentApprovalPanel = ({ approval, submissionId, onActionComplete }) => {
    const { language } = useTranslation()
    const isRTL = language === 'ar'
    const queryClient = useQueryClient()

    const [dialogOpen, setDialogOpen] = useState(false)
    const [actionType, setActionType] = useState(null) // 'approve' or 'deny'
    const [note, setNote] = useState('')

    const approveMutation = useMutation({
        mutationFn: ({ approvalId, note }) => approvalApi.approve(approvalId, note),
        onSuccess: () => {
            toast.success(isRTL ? 'تمت الموافقة بنجاح' : 'Approved successfully')
            queryClient.invalidateQueries(['submission', submissionId])
            queryClient.invalidateQueries(['pendingApprovals'])
            setDialogOpen(false)
            setNote('')
            onActionComplete?.()
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : 'Operation failed'))
        },
    })

    const denyMutation = useMutation({
        mutationFn: ({ approvalId, note }) => approvalApi.deny(approvalId, note),
        onSuccess: () => {
            toast.success(isRTL ? 'تم الرفض بنجاح' : 'Denied successfully')
            queryClient.invalidateQueries(['submission', submissionId])
            queryClient.invalidateQueries(['pendingApprovals'])
            setDialogOpen(false)
            setNote('')
            onActionComplete?.()
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || (isRTL ? 'فشلت العملية' : 'Operation failed'))
        },
    })

    const handleAction = (type) => {
        setActionType(type)
        setDialogOpen(true)
    }

    const handleConfirm = () => {
        if (actionType === 'approve') {
            approveMutation.mutate({ approvalId: approval.id, note })
        } else {
            denyMutation.mutate({ approvalId: approval.id, note })
        }
    }

    const isLoading = approveMutation.isPending || denyMutation.isPending

    if (!approval || approval.status !== 'pending') {
        return null
    }

    return (
        <>
            <Paper sx={{ p: 2, mt: 2, border: '2px solid', borderColor: 'primary.main' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {isRTL ? 'إجراء القسم' : 'Department Action'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isRTL
                        ? 'هذا الطلب بحاجة إلى موافقتك. يرجى مراجعة التفاصيل واتخاذ إجراء.'
                        : 'This submission requires your approval. Please review the details and take action.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUp />}
                        onClick={() => handleAction('approve')}
                        disabled={isLoading}
                    >
                        {isRTL ? 'موافقة' : 'Approve'}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<ThumbDown />}
                        onClick={() => handleAction('deny')}
                        disabled={isLoading}
                    >
                        {isRTL ? 'رفض' : 'Deny'}
                    </Button>
                </Box>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={dialogOpen} onClose={() => !isLoading && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {actionType === 'approve'
                        ? (isRTL ? 'تأكيد الموافقة' : 'Confirm Approval')
                        : (isRTL ? 'تأكيد الرفض' : 'Confirm Denial')}
                </DialogTitle>
                <DialogContent>
                    <Alert
                        severity={actionType === 'approve' ? 'success' : 'error'}
                        sx={{ mb: 2 }}
                    >
                        {actionType === 'approve'
                            ? (isRTL ? 'أنت على وشك الموافقة على هذا الطلب.' : 'You are about to approve this submission.')
                            : (isRTL ? 'أنت على وشك رفض هذا الطلب.' : 'You are about to deny this submission.')}
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={isRTL ? 'ملاحظة (اختياري)' : 'Note (optional)'}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={isRTL ? 'أضف ملاحظة أو سبب...' : 'Add a note or reason...'}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={isLoading}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color={actionType === 'approve' ? 'success' : 'error'}
                        onClick={handleConfirm}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={16} /> : null}
                    >
                        {actionType === 'approve'
                            ? (isRTL ? 'تأكيد الموافقة' : 'Confirm Approval')
                            : (isRTL ? 'تأكيد الرفض' : 'Confirm Denial')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default DepartmentApprovalPanel
